import { RESOURCE } from '@src/constants';
import { omitIsNil } from '@src/utils/omit';
import { API_URL } from '@src/configs';
import api from './api';

// Axios writes an array as `key[]=a&key[]=b`, but /api/v3/voices expects the
// key repeated bare: `codes=a&codes=b`. URLSearchParams emits exactly that.
const serializeRepeatedParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value))
      value.forEach((item) => searchParams.append(key, item));
    else searchParams.append(key, value);
  });
  return searchParams.toString();
};

const getVoices = async ({
  search,
  languageCode,
  gender,
  features,
  level,
  offset,
  limit,
  active,
  isSample,
  sort,
  hasDubbing,
  code,
}) => {
  const params = omitIsNil({
    search,
    languageCode,
    gender,
    features,
    level,
    offset,
    limit,
    active,
    isSample,
    sort,
    hasDubbing,
    code,
  });

  const response = await api({
    method: 'GET',
    url: RESOURCE.VOICES,
    params,
  });
  return response;
};

const getVoicesV2 = async ({
  search,
  languageCode,
  gender,
  features,
  level,
  offset,
  limit,
  active,
  isSample,
  sort,
  hasDubbing,
  code,
  voiceOwnership,
}) => {
  const params = omitIsNil({
    search,
    languageCode,
    gender,
    features,
    level,
    offset,
    limit,
    active,
    isSample,
    sort,
    hasDubbing,
    code,
    voiceOwnership,
  });

  const response = await api({
    method: 'GET',
    url: `${API_URL}/api/v2${RESOURCE.VOICES}`,
    params,
  });
  return response;
};

const getLanguages = async ({ search, searchFields }) => {
  const params = omitIsNil({
    search,
    searchFields,
  });

  const response = await api({
    method: 'GET',
    url: RESOURCE.LANGUAGES,
    params,
  });
  return response;
};

const createDictionary = async (words) => {
  try {
    const response = await api({
      method: 'POST',
      url: RESOURCE.DICTIONARY,
      data: { words },
    });
    return response;
  } catch {
    return null;
  }
};

const getDictionary = async () => {
  const response = await api({
    method: 'GET',
    url: RESOURCE.DICTIONARY,
  });
  return response;
};

const getVoicesV3 = async (
  {
    limit,
    voiceOwnership,
    sort,
    scope,
    cursor,
    locale,
    region,
    language,
    category,
    ageGroup,
    gender,
    retentionDays,
    level,
    code,
    codes,
    search,
    voiceCloningType,
    hasDubbing,
    lang,
    provider,
  },
  options,
) => {
  const params = omitIsNil({
    limit,
    voiceOwnership,
    sort,
    scope,
    cursor,
    locale,
    region,
    language,
    category,
    ageGroup,
    gender,
    retentionDays,
    level,
    code,
    codes,
    search,
    voiceCloningType,
    hasDubbing,
    lang,
    provider,
  });

  const response = await api({
    method: 'GET',
    url: `${API_URL}/api/v3${RESOURCE.VOICES}`,
    params,
    // Scoped to the multi-code case on purpose: every other caller joins its
    // multi-value params with commas, which axios leaves unescaped and
    // URLSearchParams would not.
    ...(Array.isArray(codes) && { paramsSerializer: serializeRepeatedParams }),
    ...options,
  });
  return response;
};

const getVoiceCloningInTTS = async (code) => {
  try {
    const response = await api({
      method: 'GET',
      url: `${API_URL}/api/v1${RESOURCE.VOICES}/voice-cloning/${code}`,
    });
    return response;
  } catch (error) {
    return null;
  }
};

export {
  getVoices,
  getLanguages,
  createDictionary,
  getDictionary,
  getVoicesV2,
  getVoicesV3,
  getVoiceCloningInTTS,
};
